import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Building2, Archive, TrendingUp, Send, Slash, RefreshCw, Search, GitMerge } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Slice 3 — cross-club invitations audit for the Platform Administrator.
 *
 * Lists every invitation in the system regardless of tenant, with filters for
 * status, club, and free-text search. Each non-accepted row exposes Cancel
 * (pending only) and Resend (any non-accepted) actions which call into the
 * shared `_cancel_invitation` / `_resend_invitation` helpers.
 */

const STATUSES = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "expired", label: "Expired" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_PILL = {
  pending: "bg-[#F8F1DE] text-[#8a6f33] border-[#B59A5A]",
  accepted: "bg-[#E6F0E9] text-[#1A422D] border-[#1A422D]",
  expired: "bg-[#EFEAE2] text-[#6f6557] border-[#A39782]",
  cancelled: "bg-[#fbe9e9] text-[#8a1a1a] border-[#A33]",
};

export default function SuperAdminInvitationsPage() {
  const [status, setStatus] = useState("pending");
  const [clubFilter, setClubFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 500 };
      if (status && status !== "all") params.status = status;
      if (clubFilter) params.club_id = clubFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/super-admin/invitations", { params });
      setRows(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [status, clubFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Cache club list once (for the dropdown).
  useEffect(() => {
    api.get("/super-admin/clubs").then((r) => setClubs(r.data || [])).catch(() => {});
  }, []);

  const handleCancel = async (row) => {
    if (!window.confirm(`Cancel the invitation for ${row.email} at ${row.club_name}? The recipient won't be able to use the link anymore.`)) return;
    setBusyRow(row.invitation_id);
    try {
      await api.post(`/super-admin/invitations/${row.invitation_id}/cancel`);
      toast.success("Invitation cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const handleResend = async (row) => {
    if (!window.confirm(`Resend a fresh 7-day invitation to ${row.email} for ${row.club_name}? The old link will be cancelled.`)) return;
    setBusyRow(row.invitation_id);
    try {
      const { data } = await api.post(`/super-admin/invitations/${row.invitation_id}/resend`);
      if (data?.invitation_sent) {
        toast.success(`New invitation emailed to ${row.email}.`);
      } else {
        toast.message(`Token minted but email delivery failed — share manually: ${data.register_url}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, expired: 0, cancelled: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-invitations-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Send size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform</div>
              <div className="font-serif text-2xl mt-0.5">Invitations · Network-wide</div>
            </div>
          </div>
          <Link
            to="/super-admin"
            data-testid="sa-invitations-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ShieldCheck size={14} /> Back to Console
          </Link>
        </div>
      </header>

      <SuperAdminNav active="invitations" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <div>
          <div className="overline">Cross-club Invitations</div>
          <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Token ledger across every club</h2>
          <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
            Every single-use invitation token minted by every pro shop on the platform. Cancel a leaked
            link instantly or resend a fresh 7-day token in one click.
          </p>
        </div>

        {/* Filters */}
        <section data-testid="sa-invitations-filters" className="surface-card p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                data-testid={`sa-invitations-status-${s.key}`}
                onClick={() => setStatus(s.key)}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  status === s.key
                    ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
                    : "text-[#1A422D] border-[#1A422D]/30 hover:bg-[#1A422D]/5"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px] block mb-1.5">Club</label>
              <select
                data-testid="sa-invitations-club-filter"
                value={clubFilter}
                onChange={(e) => setClubFilter(e.target.value)}
                className="input-field w-full"
              >
                <option value="">All clubs</option>
                {clubs.map((c) => (
                  <option key={c.club_id} value={c.club_id}>
                    {c.name} {c.status === "deactivated" ? "(deactivated)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1.5">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
                <input
                  data-testid="sa-invitations-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="email, club, inviter…"
                  className="input-field w-full pl-9"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Counts */}
        <div data-testid="sa-invitations-counts" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CountChip label="Pending" value={counts.pending} tone="pending" />
          <CountChip label="Accepted" value={counts.accepted} tone="accepted" />
          <CountChip label="Expired" value={counts.expired} tone="expired" />
          <CountChip label="Cancelled" value={counts.cancelled} tone="cancelled" />
        </div>

        {/* Table */}
        <section className="surface-card overflow-hidden">
          {loading && <div className="p-6 text-sm text-[#7A7A7A]" data-testid="sa-invitations-loading">Loading invitations…</div>}
          {error && (
            <div className="m-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="sa-invitations-error">
              {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-[#7A7A7A]" data-testid="sa-invitations-empty">
              No invitations match these filters.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sa-invitations-table">
                <thead className="bg-[#F0EBDA] text-[#4A4A4A]">
                  <tr>
                    <Th>Email</Th>
                    <Th>Club</Th>
                    <Th>Invited by</Th>
                    <Th>Status</Th>
                    <Th>Sent</Th>
                    <Th>Expires</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.invitation_id}
                      data-testid={`sa-invitation-row-${row.invitation_id}`}
                      className="border-t border-[#E5E3D8] hover:bg-[#FAF9F2]"
                    >
                      <Td>
                        <div className="font-medium text-[#1A1A1A]">{row.email}</div>
                        {row.name_hint && <div className="text-xs text-[#7A7A7A]">{row.name_hint}</div>}
                      </Td>
                      <Td>
                        <div className="text-[#1A1A1A]">{row.club_name}</div>
                        <div className="text-xs text-[#7A7A7A]">{row.club_slug}</div>
                      </Td>
                      <Td>
                        <div className="text-xs">{row.invited_by_email || row.invited_by_name || "—"}</div>
                      </Td>
                      <Td>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border ${
                            STATUS_PILL[row.status] || "bg-white text-[#4A4A4A] border-[#4A4A4A]"
                          }`}
                        >
                          {row.status}
                        </span>
                      </Td>
                      <Td className="text-xs text-[#7A7A7A]">{fmtDate(row.created_at)}</Td>
                      <Td className="text-xs text-[#7A7A7A]">{fmtDate(row.expires_at)}</Td>
                      <Td className="text-right">
                        <RowActions
                          row={row}
                          busy={busyRow === row.invitation_id}
                          onCancel={() => handleCancel(row)}
                          onResend={() => handleResend(row)}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CountChip({ label, value, tone }) {
  return (
    <div className={`px-4 py-3 border ${STATUS_PILL[tone] || ""}`}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-serif text-2xl mt-0.5">{value || 0}</div>
    </div>
  );
}

function RowActions({ row, busy, onCancel, onResend }) {
  if (row.status === "accepted") {
    return <span className="text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">—</span>;
  }
  return (
    <div className="inline-flex items-center gap-2 justify-end">
      {row.status === "pending" && (
        <button
          data-testid={`sa-invitation-cancel-${row.invitation_id}`}
          disabled={busy}
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-[#A33]/40 text-[#A33] hover:bg-[#fbe9e9] disabled:opacity-50"
        >
          <Slash size={12} /> Cancel
        </button>
      )}
      <button
        data-testid={`sa-invitation-resend-${row.invitation_id}`}
        disabled={busy}
        onClick={onResend}
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-[#1A422D]/30 text-[#1A422D] hover:bg-[#1A422D]/5 disabled:opacity-50"
      >
        <RefreshCw size={12} /> Resend
      </button>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-3 py-2 overline text-[10px] ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/** Shared SA nav strip — kept here to avoid pulling in another shared component. */
export function SuperAdminNav({ active }) {
  const item = (key, to, Icon, label, testId) => (
    <Link to={to} data-testid={testId} className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] ${active === key ? "text-[#D9C68A]" : "text-[#D9C68A]/80 hover:text-[#D9C68A]"}`}>
      <Icon size={12} /> {label}
    </Link>
  );
  return (
    <nav className="bg-[#142019] border-b border-[#1A422D]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
        {item("clubs", "/super-admin/clubs", Building2, "Clubs", "sa-nav-clubs")}
        <span className="text-[#1A422D]">·</span>
        {item("demand", "/super-admin/demand", TrendingUp, "Demand", "sa-nav-demand")}
        <span className="text-[#1A422D]">·</span>
        {item("invitations", "/super-admin/invitations", Send, "Invitations", "sa-nav-invitations")}
        <span className="text-[#1A422D]">·</span>
        {item("merge", "/super-admin/merge", GitMerge, "Merge & Transfer", "sa-nav-merge")}
        <span className="text-[#1A422D]">·</span>
        {item("archive", "/super-admin/archive", Archive, "Archive", "sa-nav-archive")}
      </div>
    </nav>
  );
}
